import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const SLACK_ERROR_MESSAGES = {
  please_link_from_settings: 'This email already has an account. Sign in with email first, then link Slack from Settings.',
  wrong_workspace: 'You signed in from a different Slack workspace. Use your CarPal Slack workspace.',
  workspace_not_configured: 'Slack workspace not yet configured. The admin must link their Slack account first.',
  slack_account_already_linked_to_another_user: 'This Slack account is already linked to another user.',
  invalid_or_expired_state: 'Login session expired. Please try again.',
  token_exchange_failed: 'Slack token exchange failed. Please try again.',
  userinfo_failed: 'Could not fetch your Slack profile. Please try again.',
  incomplete_userinfo: 'Slack did not return a complete profile (missing email or workspace).',
  missing_code_or_state: 'Slack did not return an authorization code.',
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [slackLoading, setSlackLoading] = useState(false);

  useEffect(() => {
    const slackError = searchParams.get('slack_error');
    if (slackError) {
      const pretty = SLACK_ERROR_MESSAGES[slackError]
        || (slackError.startsWith('email_domain_')
              ? `Email domain not allowed. Only @autocarma.in and @trycarpal.com are permitted.`
              : `Slack sign-in failed: ${slackError.replace(/_/g, ' ')}`);
      setError(pretty);
      // Clear the query param so a refresh doesn't re-show the error
      searchParams.delete('slack_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSlackLogin = async () => {
    setSlackLoading(true);
    setError('');
    try {
      const res = await api.get('/auth/slack/login?intent=login');
      window.location.href = res.data.authorize_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to start Slack sign-in');
      setSlackLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-[#34495e] mb-2" data-testid="login-heading">
            Task Manager
          </h1>
          <p className="text-[#7F8C8D] mb-8 text-sm leading-relaxed">Sign in to manage your team's tasks</p>

          {error && (
            <div className="mb-4 rounded-sm border border-red-200 bg-red-50 p-3 text-xs text-red-700" data-testid="login-error">
              {error}
            </div>
          )}

          {/* PRIMARY: Sign in with Slack */}
          <Button
            onClick={handleSlackLogin}
            disabled={slackLoading}
            className="w-full bg-[#4A154B] hover:bg-[#3e1040] text-white rounded-sm h-11 font-bold tracking-wide"
            data-testid="slack-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.712.133a5.381 5.381 0 00-5.376 5.387 5.381 5.381 0 005.376 5.386h5.376V5.52A5.381 5.381 0 0019.712.133m0 14.365H5.376A5.381 5.381 0 000 19.884a5.381 5.381 0 005.376 5.387h14.336a5.381 5.381 0 005.376-5.387 5.381 5.381 0 00-5.376-5.386" fill="#36C5F0"/>
              <path d="M53.76 19.884a5.381 5.381 0 00-5.376-5.386 5.381 5.381 0 00-5.376 5.386v5.387h5.376a5.381 5.381 0 005.376-5.387m-14.336 0V5.52A5.381 5.381 0 0034.048.133a5.381 5.381 0 00-5.376 5.387v14.364a5.381 5.381 0 005.376 5.387 5.381 5.381 0 005.376-5.387" fill="#2EB67D"/>
              <path d="M34.048 54a5.381 5.381 0 005.376-5.387 5.381 5.381 0 00-5.376-5.386h-5.376v5.386A5.381 5.381 0 0034.048 54m0-14.365h14.336a5.381 5.381 0 005.376-5.386 5.381 5.381 0 00-5.376-5.387H34.048a5.381 5.381 0 00-5.376 5.387 5.381 5.381 0 005.376 5.386" fill="#ECB22E"/>
              <path d="M0 34.249a5.381 5.381 0 005.376 5.386 5.381 5.381 0 005.376-5.386v-5.387H5.376A5.381 5.381 0 000 34.25m14.336 0v14.364A5.381 5.381 0 0019.712 54a5.381 5.381 0 005.376-5.387V34.25a5.381 5.381 0 00-5.376-5.387 5.381 5.381 0 00-5.376 5.387" fill="#E01E5A"/>
            </svg>
            {slackLoading ? 'Redirecting to Slack...' : 'Continue with Slack'}
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#E2E8F0]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white text-[#7F8C8D] uppercase tracking-wider font-bold">Or sign in with email</span>
            </div>
          </div>

          {/* SECONDARY: email + password (for non-Slack users) */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@trycarpal.com"
                className="mt-1 rounded-sm border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#34495e]"
                data-testid="login-email-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-[#7F8C8D]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1 rounded-sm border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#34495e]"
                data-testid="login-password-input"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              variant="outline"
              className="w-full rounded-sm border-[#34495e] text-[#34495e] hover:bg-[#34495e] hover:text-white"
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In with Email'}
            </Button>
          </form>

          <p className="mt-6 text-xs text-[#BDC3C7] text-center">
            Only @autocarma.in and @trycarpal.com emails are allowed
          </p>
        </div>
      </div>

      {/* Right - Background Image */}
      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_weekly-task-pack/artifacts/y2r5m645_CarPal%20-%20Images%20with%20Frame.png"
          alt="CarPal"
          className="absolute inset-0 w-full h-full object-cover"
        />
      </div>
    </div>
  );
}
